interface AppData {
  transactions: any[];
  agencies: any[];
  negotiators: any[];
  assistants: any[];
}

export const generateSystemPrompt = (appData: AppData) => {
  // Calculer les statistiques de base
  const activeTransactions = appData.transactions.filter(t => !['cloture', 'annule'].includes(t.status));
  const potentialTransactions = appData.transactions.filter(t => t.status === 'potentiel');
  const compromisTransactions = appData.transactions.filter(t => t.status === 'compromis');
  const acteTransactions = appData.transactions.filter(t => t.status === 'acte');
  const encaisseTransactions = appData.transactions.filter(t => t.status === 'encaisse');

  // Calculer le total des ventes et honoraires
  const totalSales = activeTransactions.reduce((sum, t) => sum + t.sale_price, 0);
  const totalFees = activeTransactions.reduce((sum, t) => sum + t.fees_without_tax, 0);

  // Formatter les données pour le prompt
  const formattedData = {
    transactions: appData.transactions.map(t => ({
      id: t.id,
      property_number: t.property_number,
      status: t.status,
      mandate_type: t.mandate_type,
      seller: t.seller,
      buyer: t.buyer,
      sale_price: t.sale_price,
      fees_with_tax: t.fees_with_tax,
      fees_without_tax: t.fees_without_tax,
      compromise_date: t.compromise_date,
      expected_acte_date: t.expected_acte_date,
      acte_date: t.acte_date,
      invoice_date: t.invoice_date,
      invoice_number: t.invoice_number,
      encaissement_date: t.encaissement_date,
      agencies: t.transaction_agencies?.map((ta: any) => ta.agency),
      negotiators: t.transaction_negotiators?.map((tn: any) => ({
        name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
        role: tn.role,
        percentage: tn.percentage
      })),
      assistants: t.transaction_assistants?.map((ta: any) => ({
        name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
        role: ta.role
      })),
      retrocessions: t.transaction_retrocessions?.map((tr: any) => ({
        type: tr.type,
        amount: tr.amount,
        payment_date: tr.payment_date
      }))
    })),
    agencies: appData.agencies.map(a => ({
      name: a.name,
      company: a.company,
      active: a.active
    })),
    negotiators: appData.negotiators.map(n => ({
      id: n.id,
      name: `${n.firstname} ${n.lastname}`,
      role: n.role,
      status: n.status,
      agencies: n.negotiator_agencies?.map((na: any) => na.agency)
    })),
    assistants: appData.assistants.map(a => ({
      id: a.id,
      name: `${a.firstname} ${a.lastname}`,
      agencies: a.assistant_agencies?.map((aa: any) => aa.agency)
    }))
  };

  return `Tu es K-Bot, un assistant spécialisé exclusivement dans l'analyse des données de K-PROD, l'application de gestion des transactions immobilières du groupe Kermarrec.

RÈGLES CRITIQUES À SUIVRE ABSOLUMENT :
1. Ne JAMAIS inventer ou extrapoler des données qui ne sont pas dans l'application
2. Si une information demandée n'est pas disponible, le dire clairement
3. Toujours baser les réponses sur les données réelles fournies
4. Ne pas faire de suppositions sur des données manquantes
5. Pour les statistiques, utiliser uniquement les chiffres calculés à partir des données
6. Toujours vérifier l'existence des données avant de répondre
7. Si une donnée n'existe pas, indiquer "Aucune donnée trouvée" plutôt que "0"
8. Pour chaque réponse, citer les sources exactes des données
9. Analyse 2 fois les données pour être sur de ne pas te tromper dans les réponses.
10. KA est l'acronyme de Kermarrec Atlantique. 
11. KI est l'acronyme de Kermarrec Immobilier et kermarrec Habitation.

STRUCTURE DES RÉPONSES :
1. Commencer par une réponse directe à la question
2. Détailler les informations si nécessaire
3. Indiquer la source des données (ex: "Selon les transactions enregistrées dans K-PROD...")
4. Si pertinent, proposer des analyses complémentaires

FORMATAGE DES DONNÉES :
- Montants : format français avec € (ex: 150 000 €)
- Dates : format français (ex: 15/01/2024)
- Pourcentages : avec symbole % (ex: 25%)

STATISTIQUES GLOBALES :
- Transactions actives : ${activeTransactions.length}
  * Potentiel : ${potentialTransactions.length}
  * Compromis : ${compromisTransactions.length}
  * Acte : ${acteTransactions.length}
  * Encaissé : ${encaisseTransactions.length}
- Volume total des ventes : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totalSales)}
- Total des honoraires HT : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totalFees)}
- Agences actives : ${appData.agencies.length}
- Négociateurs : ${appData.negotiators.length}
- Assistants : ${appData.assistants.length}

DONNÉES DISPONIBLES :
${JSON.stringify(formattedData, null, 2)}

IMPORTANT : Ne jamais inventer de données. Si une information n'est pas disponible dans les données fournies, le dire explicitement.`;
};